import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { fetchAdminPaymentRequests, rejectPaymentRequest } from '../../redux/adminPaymentSlice';
import { useNotifications } from '../../hooks/useNotifications';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AdminSidebar from '../../components/sidebar/AdminSidebar';
import StatusBadge from '../../components/StatusBadge';
import ApprovalWorkflowDrawer from '../../components/dashboard/ApprovalWorkflowDrawer';
import { paymentsApi } from '../../services/api';
import './PaymentRequestsPage.css';

const PaymentRequestsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useNotifications();
  const { paymentRequests, loading } = useSelector((state: RootState) => state.adminPayment);
  const [selectedRequestId, setSelectedRequestId] = React.useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchAdminPaymentRequests());
  }, [dispatch]);

  const handleReject = async (requestId: string, rejectionReason: string) => {
    try {
      await dispatch(rejectPaymentRequest({ paymentId: requestId, rejectionReason })).unwrap();
      notifications.success('Payment request rejected.');
    } catch (error) {
      console.error('Failed to reject payment request:', error);
      notifications.error('Failed to reject payment request. Please try again.');
    }
  };

  const handleDebitWallet = async (requestId: string) => {
    try {
      const result = await paymentsApi.debitWalletForPayment(requestId);

      if (result.success) {
        notifications.success('Wallet debited successfully. Payment moved to processing.');
        dispatch(fetchAdminPaymentRequests());
      } else {
        notifications.error(result.data?.message || 'Failed to debit wallet. Please try again.');
      }
    } catch (error) {
      console.error('Failed to debit wallet:', error);
      notifications.error('Failed to debit wallet. Please try again.');
    }
  };

  const handleRetryReap = async (requestId: string) => {
    try {
      const result = await paymentsApi.retryReapSubmission(requestId);
      const reapStatus = result.data?.reapStatus;
      const reapError = result.data?.reapError;

      if (reapStatus === 'sent') {
        notifications.success('Reap submission succeeded. You can now debit wallet.');
      } else {
        notifications.warning(
          `Reap retry status is ${reapStatus || 'unknown'}. ${reapError || 'Check row details.'}`
        );
      }

      dispatch(fetchAdminPaymentRequests());
    } catch (error) {
      console.error('Failed to retry Reap submission:', error);
      notifications.error('Failed to retry Reap submission. Please try again.');
    }
  };

  const handleViewInvoice = async (paymentId: string) => {
    try {
      const { invoiceUrl } = await paymentsApi.getPaymentInvoiceUrl(paymentId);
      window.open(invoiceUrl, '_blank');
    } catch (error) {
      console.error('Failed to open invoice:', error);
      notifications.error('Failed to open invoice. Please try again.');
    }
  };

  const formatCurrency = (amount: number, currencyCode: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const [customFxRate, setCustomFxRate] = React.useState<string>('');
  const selectedRequest = paymentRequests.find((request: any) => request._id === selectedRequestId);

  const handleApproveWithFx = async (requestId: string) => {
    try {
      const parsedFx = customFxRate ? parseFloat(customFxRate) : undefined;
      const result = await paymentsApi.approvePayment(requestId, parsedFx);
      if (result.data?.reapStatus && result.data?.reapStatus !== 'sent') {
        notifications.warning(
          `Payment approved, but Reap status is ${result.data.reapStatus}. ${result.data.reapError || 'Check details.'}`
        );
      } else {
        notifications.success('Payment approved and submitted to Reap.');
      }
      setSelectedRequestId(null);
      dispatch(fetchAdminPaymentRequests());
    } catch (error: any) {
      console.error('Failed to approve payment request:', error);
      notifications.error(error?.response?.data?.message || 'Failed to approve payment request.');
    }
  };

  return (
    <DashboardLayout sidebar={<AdminSidebar />}>
      <div className="payment-requests-page">
        <div className="page-header">
          <h2>Payment Requests</h2>
          <p>Review international payment requests with automated OhMyFinAI compliance checks</p>
        </div>

        <div className="payment-requests-container">
          <table className="payment-requests-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Recipient</th>
                <th>Bank</th>
                <th>Amount</th>
                <th>Local Amount</th>
                <th>Compliance</th>
                <th>Invoice</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paymentRequests.map((request: any) => (
                <tr key={request._id}>
                  <td>
                    <div className="user-info">
                      <div className="user-name">{request.userId?.name || 'User'}</div>
                      <div className="company-name">{request.userId?.email || 'Company'}</div>
                    </div>
                  </td>
                  <td className="recipient-info">
                    <div>{request.recipientCompany}</div>
                    <div className="account-number">{request.accountNumber}</div>
                  </td>
                  <td className="bank-name">{request.recipientBank}</td>
                  <td className="foreign-amount">
                    {formatCurrency(request.foreignAmount, request.foreignCurrency)}
                  </td>
                  <td className="local-amount">
                    {formatCurrency(request.localAmount, 'NGN')}
                  </td>
                  <td className="compliance-cell">
                    <span className={`status-badge ${request.complianceAction === 'BLOCK' ? 'danger' : 'success'}`} style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}>
                      {request.complianceAction || 'CLEAR'} ({request.complianceReportId || 'SCR-OK'})
                    </span>
                  </td>
                  <td className="invoice-file">
                    {request.invoiceUrl ? (
                      <button
                        onClick={() => handleViewInvoice(request._id!)}
                        className="view-invoice-btn"
                      >
                        View Invoice
                      </button>
                    ) : (
                      <span className="file-missing">Missing</span>
                    )}
                  </td>
                  <td className="submitted-date">
                    {request.createdAt ? new Date(request.createdAt).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="status-cell">
                    <div className="status-column">
                      <StatusBadge status={request.status} type="payment" />
                      {request.reapStatus && (
                        <div className="reap-status">
                          <small className={`reap-${request.reapStatus}`}>Reap: {request.reapStatus}</small>
                          {request.reapError && (
                            <small className="reap-error-message">{request.reapError}</small>
                          )}
                          {request.reapErrorMessage && (
                            <small className="reap-error-message">{request.reapErrorMessage}</small>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="actions">
                    {(request.status === 'pending_admin_approval' || request.status === 'compliance_hold') && (
                      <button
                        onClick={() => {
                          setSelectedRequestId(request._id!);
                          setCustomFxRate(request.exchangeRate ? String(request.exchangeRate) : '');
                        }}
                        className="approve-btn"
                      >
                        Review & Set FX
                      </button>
                    )}
                    {request.status === 'approved' && request.reapStatus === 'sent' && (
                      <button
                        onClick={() => handleDebitWallet(request._id!)}
                        className="debit-btn"
                      >
                        Debit Wallet
                      </button>
                    )}
                    {request.status === 'approved' && request.reapStatus !== 'sent' && (
                      <button
                        onClick={() => handleRetryReap(request._id!)}
                        className="retry-btn"
                      >
                        Retry Reap
                      </button>
                    )}
                    {request.status !== 'pending_admin_approval' && request.status !== 'compliance_hold' && request.status !== 'approved' && (
                      <span className="decision-made">
                        {request.status.toUpperCase()}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading ? (
          <div className="loading">
            <p>Loading payment requests...</p>
          </div>
        ) : paymentRequests.length === 0 ? (
          <div className="no-requests">
            <p>No payment requests pending at this time.</p>
          </div>
        ) : null}

        <ApprovalWorkflowDrawer
          isOpen={Boolean(selectedRequest)}
          title="Payment Approval & FX Rate Override"
          subtitle="Review OhMyFinAI compliance verification certificate and set official manual FX rate before final approval."
          onClose={() => setSelectedRequestId(null)}
          contextItems={
            selectedRequest
              ? [
                  { label: 'Recipient', value: (selectedRequest as any).recipientCompany },
                  { label: 'Account Number', value: (selectedRequest as any).accountNumber },
                  {
                    label: 'Amount',
                    value: formatCurrency((selectedRequest as any).localAmount, 'NGN'),
                  },
                  {
                    label: 'OhMyFinAI Audit Report',
                    value: (selectedRequest as any).complianceReportId || 'SCR-00012345',
                  },
                  {
                    label: 'Compliance Verdict',
                    value: (selectedRequest as any).complianceAction || 'CLEAR',
                  },
                ]
              : []
          }
          onApprove={
            selectedRequest
              ? () => handleApproveWithFx(selectedRequest._id!)
              : undefined
          }
          onReject={
            selectedRequest
              ? (reason) => handleReject(selectedRequest._id!, reason)
              : undefined
          }
          rejectionLabel="Rejection Notes"
          rejectionPlaceholder="State specific compliance or payment issues for the customer."
        >
          {selectedRequest && (
            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                💱 Admin Manual FX Rate (1 {selectedRequest.foreignCurrency} to NGN):
              </label>
              <input
                type="number"
                step="0.01"
                className="ohmyfin-input"
                style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                placeholder="Enter manual exchange rate (e.g. 1550.00)..."
                value={customFxRate}
                onChange={(e) => setCustomFxRate(e.target.value)}
              />
              <small style={{ color: '#64748b', display: 'block', marginTop: '0.25rem' }}>
                Note: Ohmyfin reference FX rates are restricted to Admin review. This rate will override the customer estimate.
              </small>
            </div>
          )}
        </ApprovalWorkflowDrawer>
      </div>
    </DashboardLayout>
  );
};

export default PaymentRequestsPage;
